import { useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useQuery } from "@tanstack/react-query";

import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import {
  PortalAlert,
  PortalButton,
  PortalCard,
  PortalEmpty,
  PortalGrid,
  PortalGridItem,
  PortalLinkRow,
  PortalLoading,
  PortalStatusChip,
} from "components/portal/PortalUi";
import { useAuth } from "context/AuthContext";
import { get, post } from "lib/api";
import { isDistributorRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";

function pretty(value, fallback = "Pending") {
  return String(value || fallback)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function dateText(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString();
}

function money(value, currency = "USD") {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: String(currency || "USD").toUpperCase(),
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function requestTotal(request) {
  const totals =
    request?.totalsSnapshot && typeof request.totalsSnapshot === "object"
      ? request.totalsSnapshot
      : {};
  return money(
    totals.total ?? totals.grandTotal ?? totals.orderTotal ?? totals.subtotal,
    totals.currency || "USD",
  );
}

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "submitted", label: "Submitted" },
  { key: "under_review", label: "Under review" },
  { key: "fulfilled_by_distributor", label: "Fulfilled" },
  { key: "redirected_to_warehouse", label: "Sent to VBS" },
];

export default function CustomerDistributorRequests() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useStorefrontCopy();
  const [status, setStatus] = useState("");
  const role = String(pluginRole || "").toLowerCase();
  const isDistributor = isDistributorRole(role);

  const query = useQuery({
    queryKey: ["portal-distributor-requests", status],
    queryFn: () =>
      get("/api/storefront/distributor-orders/requests", {
        pagination: { page: 1, pageSize: 50 },
        ...(status ? { status } : {}),
      }),
    enabled: !!token && isDistributor,
    staleTime: 10000,
  });

  if (!token) {
    return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  }
  if (authLoading || pluginRole == null) return null;
  if (!isDistributor) return <Navigate to="/account" replace />;

  const rows = Array.isArray(query.data?.data) ? query.data.data : [];

  return (
    <CustomerPortalLayout
      title={t("portal.distributorRequests", "Order requests")}
      subtitle={t(
        "portal.distributorRequestsSubtitle",
        "Review customer requests assigned to your distributor account.",
      )}
    >
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, mb: 2 }}>
        {STATUS_FILTERS.map((filter) => (
          <PortalButton
            key={filter.key || "all"}
            size="sm"
            variant={status === filter.key ? "primary" : "ghost"}
            onClick={() => setStatus(filter.key)}
          >
            {filter.label}
          </PortalButton>
        ))}
      </Stack>

      {query.error ? (
        <PortalAlert severity="error">
          {query.error?.response?.data?.error?.message ||
            query.error?.message ||
            t("portal.loadError", "Failed to load data.")}
        </PortalAlert>
      ) : null}

      {query.isLoading ? (
        <PortalLoading label={t("common.loading", "Loading…")} />
      ) : !rows.length ? (
        <PortalEmpty
          message={t(
            "portal.distributorRequestsEmpty",
            "No distributor order requests yet.",
          )}
        />
      ) : (
        <Stack spacing={1}>
          {rows.map((request) => (
            <PortalLinkRow
              key={String(request.documentId)}
              title={request.requestingCustomer?.name || t("portal.customerRequest", "Customer request")}
              meta={`${dateText(request.requestedAt || request.createdAt)} · ${request.lineCount || 0} item(s)`}
              trailing={<PortalStatusChip label={pretty(request.status)} tone="accent" />}
              onClick={() => navigate(`/account/distributor-requests/${request.documentId}`)}
            />
          ))}
        </Stack>
      )}
    </CustomerPortalLayout>
  );
}

export function CustomerDistributorRequestDetail() {
  const { documentId } = useParams();
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useStorefrontCopy();
  const role = String(pluginRole || "").toLowerCase();
  const isDistributor = isDistributorRole(role);
  const [decisionReason, setDecisionReason] = useState("");
  const [savingDecision, setSavingDecision] = useState("");
  const [notice, setNotice] = useState(null);

  const query = useQuery({
    queryKey: ["portal-distributor-request", documentId],
    queryFn: () => get(`/api/storefront/distributor-orders/requests/${documentId}`),
    enabled: !!token && isDistributor && !!documentId,
    staleTime: 10000,
  });

  if (!token) {
    return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  }
  if (authLoading || pluginRole == null) return null;
  if (!isDistributor) return <Navigate to="/account" replace />;

  const request = query.data?.data || null;
  const lines = Array.isArray(request?.lines) ? request.lines : [];
  const totals =
    request?.totalsSnapshot && typeof request.totalsSnapshot === "object"
      ? request.totalsSnapshot
      : {};
  const status = String(request?.status || "").toLowerCase();
  const canDecide = status === "submitted" || status === "under_review";

  const decide = async (decisionType) => {
    try {
      setSavingDecision(decisionType);
      setNotice(null);
      await post(`/api/storefront/distributor-orders/requests/${documentId}/decision`, {
        data: { decisionType, decisionReason: decisionReason.trim() || null },
      });
      setNotice({
        type: "success",
        text:
          decisionType === "redirect_warehouse"
            ? t("portal.requestSentToVbs", "Request sent to VBS.")
            : t("portal.requestFulfilled", "Request marked fulfilled."),
      });
      await query.refetch();
    } catch (error) {
      setNotice({
        type: "error",
        text:
          error?.response?.data?.error?.message ||
          error?.message ||
          t("portal.requestUpdateError", "Unable to update request."),
      });
    } finally {
      setSavingDecision("");
    }
  };

  return (
    <CustomerPortalLayout
      title={t("portal.orderRequest", "Order request")}
      subtitle={request?.requestingCustomer?.name || t("portal.customerRequest", "Customer request")}
      actions={
        <PortalButton variant="ghost" startIcon="arrow_back" onClick={() => navigate("/account/distributor-requests")}>
          {t("common.back", "Back")}
        </PortalButton>
      }
    >
      {notice ? <PortalAlert severity={notice.type}>{notice.text}</PortalAlert> : null}

      {query.isLoading ? (
        <PortalLoading />
      ) : !request ? (
        <PortalEmpty message={t("portal.requestNotFound", "Order request not found.")} />
      ) : (
        <PortalGrid>
          <PortalGridItem xs={12} md={7}>
            <PortalCard
              title={t("portal.requestDetails", "Request details")}
              subtitle={`${lines.length} line item(s)`}
            >
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, mb: 2 }}>
                <PortalStatusChip label={pretty(request.status)} tone="accent" />
                <PortalStatusChip label={requestTotal(request)} />
              </Stack>
              <Stack spacing={0.75}>
                <Box component="p" sx={{ m: 0, fontSize: "0.875rem" }}>
                  <strong>{t("portal.customer", "Customer")}:</strong>{" "}
                  {request.requestingCustomer?.name || "—"}
                </Box>
                <Box component="p" sx={{ m: 0, fontSize: "0.875rem" }}>
                  <strong>{t("portal.requested", "Requested")}:</strong>{" "}
                  {dateText(request.requestedAt || request.createdAt)}
                </Box>
                <Box component="p" sx={{ m: 0, fontSize: "0.875rem" }}>
                  <strong>{t("portal.decision", "Decision")}:</strong>{" "}
                  {pretty(request.decisionType || "Pending")}
                </Box>
              </Stack>
            </PortalCard>
          </PortalGridItem>

          <PortalGridItem xs={12} md={5}>
            <PortalCard title={t("checkout.shipping", "Shipping")}>
              <Box component="p" sx={{ m: 0, fontSize: "0.875rem", lineHeight: 1.55 }}>
                {[
                  request.shipToSnapshot?.name,
                  request.shipToSnapshot?.line1,
                  request.shipToSnapshot?.line2,
                  request.shipToSnapshot?.city,
                  request.shipToSnapshot?.region || request.shipToSnapshot?.state,
                  request.shipToSnapshot?.postalCode,
                  request.shipToSnapshot?.country,
                ]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </Box>
            </PortalCard>
          </PortalGridItem>

          <PortalGridItem xs={12}>
            <PortalCard title={t("portal.lineItems", "Line items")} accentTop={false}>
              <Stack spacing={1}>
                {lines.map((line, index) => {
                  const itemName =
                    line.name ||
                    line.productName ||
                    line.inventoryName ||
                    line.inventoryItem?.name ||
                    "Item";
                  const sku = line.sku || line.inventorySku || line.inventoryItem?.sku || "";
                  const quantity = Number(line.quantity || line.qty || 1);
                  const unitPrice = line.unitPrice ?? line.price ?? line.sellPrice ?? 0;
                  const coreCharge = line.coreCharge ?? line.coreDeposit ?? 0;
                  return (
                    <Box
                      key={String(line.documentId || line.id || index)}
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        border: "1px solid rgba(17,19,24,0.1)",
                        bgcolor: "#F7F8FA",
                      }}
                    >
                      <Box sx={{ fontWeight: 800 }}>
                        {sku ? `${sku} · ` : ""}
                        {itemName}
                      </Box>
                      <Box sx={{ fontSize: "0.8125rem", color: "#5C6370", mt: 0.35 }}>
                        Qty {quantity} · Unit {money(unitPrice, totals.currency)} · Core{" "}
                        {money(coreCharge, totals.currency)}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </PortalCard>
          </PortalGridItem>

          {canDecide ? (
            <PortalGridItem xs={12}>
              <PortalCard title={t("portal.distributorDecision", "Distributor decision")}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("portal.notes", "Notes")}
                  value={decisionReason}
                  onChange={(event) => setDecisionReason(event.target.value)}
                  sx={{ mb: 1.5 }}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <PortalButton
                    variant="primary"
                    disabled={!!savingDecision}
                    onClick={() => decide("fulfill_self")}
                  >
                    {savingDecision === "fulfill_self"
                      ? t("common.loading", "Saving…")
                      : t("portal.fulfillSelf", "Fulfill from distributor")}
                  </PortalButton>
                  <PortalButton
                    variant="secondary"
                    disabled={!!savingDecision}
                    onClick={() => decide("redirect_warehouse")}
                  >
                    {savingDecision === "redirect_warehouse"
                      ? t("common.loading", "Sending…")
                      : t("portal.sendToVbs", "Send to VBS warehouse")}
                  </PortalButton>
                </Stack>
              </PortalCard>
            </PortalGridItem>
          ) : null}
        </PortalGrid>
      )}
    </CustomerPortalLayout>
  );
}
